import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItem,
	Stack,
	TextField,
	Typography
} from "@mui/material"
import {useDispatch, useSelector} from "react-redux"
import {
	resetTopLevelNotePermissions,
	selectTargetUser,
	selectTopLevelNotes,
	setTopLevelNotePermissionsAllRead,
	setTopLevelNotePermissionsAllEdit,
	fetchTopLevelNotesForManagement,
	clearManageWorkspaceView,
	modifyExistingWorkspaceMember,
	selectIsAddingNewMember,
	addNewWorkspaceMember,
	selectIsInvitingAuthor,
	generateAuthorInvite,
	selectIsModifyingAuthor, modifyAuthorInvite, setManageWorkspaceMessage, setManageWorkspaceError
} from "../../reducers/manageWorkspaceSlice"
import NoteRestrictionBreadcrumb from "./NoteRestrictionBreadcrumb"
import {useEffect, useRef, useState} from "react"
import {ManageModalContainer} from "./shared_components/ManageModalContainer"

export default function ConfigureNotePermissions() {
	const topLevelNotes = useSelector(selectTopLevelNotes)
	const dispatch = useDispatch()
	const member = useSelector(selectTargetUser)
	const isReadOnly = member && member.workspacePermissions.write === false
	const isAddingNewMember = useSelector(selectIsAddingNewMember)
	const isInvitingAuthor = useSelector(selectIsInvitingAuthor)
	const isModifyingAuthor = useSelector(selectIsModifyingAuthor)
	const [authorInvite, setAuthorInvite] = useState(null)
	const generatedKeyInputRef = useRef()

	useEffect(() => {
		dispatch(fetchTopLevelNotesForManagement())
	}, [])

	const handleSubmit = () => {
		if(isAddingNewMember) {
			dispatch(addNewWorkspaceMember(member))
		}

		if(isInvitingAuthor) {
			dispatch(generateAuthorInvite(member))
				.then(({payload: authorKeyDTO}) => {
					setAuthorInvite(authorKeyDTO)
				})
		}

		if(isModifyingAuthor) {
			dispatch(modifyAuthorInvite())
				.then(({payload: authorKeyDTO}) => {
					setAuthorInvite(authorKeyDTO)
				})
		}

		if(!isAddingNewMember && !isInvitingAuthor && !isModifyingAuthor) {
			dispatch(modifyExistingWorkspaceMember(member))
		}
	}


	const copyInviteLink = (inviteLink) => {
		navigator.clipboard
			.writeText(inviteLink)
			.then(() => {
				dispatch(setManageWorkspaceMessage("Author invite copied to clipboard"))
			})
			.catch((e) => {
				console.error ("ERROR WRITING LINK TO CLIPBOARD: ", e)
				dispatch(setManageWorkspaceError("Failed to copy author invite link"))
			})
	}

	useEffect(() => {
		if(authorInvite && authorInvite.inviteUUID && generatedKeyInputRef.current) {
			generatedKeyInputRef.current.focus()
		}
	}, [authorInvite])

	return (
		<ManageModalContainer>
			<DialogTitle>{ authorInvite === null ? "Configure Visible Notes" : "Author Invite Code"}</DialogTitle>
			<DialogContent>
				{ authorInvite === null &&
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "flex-start",
							gap: "1rem",
							padding: "0px 20px",
							//TODO: this is dumb, but apparently the ONLY way to remove x-axis scrollbar
							overflow: "hidden"
						}}
					>

						<NotePermissionsControls isReadOnly={isReadOnly}/>
						<Box sx={{
							display: "flex",
							gap: "0.5rem",
							alignItems: "center",
							width: "98%",
							paddingRight: "2.5rem",
							justifyContent: "flex-end",
							marginBottom: "-1rem",
							backgroundColor: "none",
						}}>
							<Typography variant={"tunedSubtitle2"} sx={{fontSize: "0.95em", lineHeight: "1rem", color: "action.active"}}>
								View
							</Typography>
							<Typography variant={"tunedSubtitle2"} sx={{fontSize: "0.95em", lineHeight: "1rem", color: "action.active"}}>
								Edit
							</Typography>
						</Box>
						{/*
						Top Level Note List
					*/}
						{ topLevelNotes.length > 0 &&
							<List
								sx={{
									width: "100%",
									maxHeight: "45vh",
									overflowY: "auto",
									padding: "8px 0.5rem"
								}}
							>
								{
									topLevelNotes.map((note, i) => {
										return (
											<ListItem
												key={i}
												sx={{
													padding: "0px",
													paddingBottom: "0.75rem",
													display: "block",
													pointerEvents: "auto",
												}}>
												<NoteRestrictionBreadcrumb
													note={note}
													readonly={isReadOnly}
												/>
											</ListItem>
										)
									})
								}
							</List>
						}
					</Box>
				}
				{authorInvite && authorInvite.inviteUUID &&
					<Stack gap={2} sx={{padding: "20px 20px"}}>
						<TextField
							inputRef={generatedKeyInputRef}
							defaultValue={authorInvite.inviteUUID}
							value={authorInvite.inviteUUID}
							fullWidth
							inputProps={{
								style: {
									textAlign: "center"
								}
							}}
							slotProps={{
								input: {
									readOnly: true,
								},
							}}
						/>
						<Box sx={{
							display: "flex",
							flexDirection: "row",
							justifyContent: "center",
							gap: "1rem"
						}}>
							<Button onClick={() => copyInviteLink(authorInvite.inviteUUID)} variant={"contained"}>
								Copy raw access code
							</Button>
							<Button onClick={() => copyInviteLink(encodeURI(authorInvite.inviteLink))} variant={"outlined"}>
								Copy a formatted link
							</Button>
							{/*
								// Legacy: open e-mail client with body pre-filled
							*/}
							{/*<Button*/}
							{/*	onClick={() => window.location = `mailto:${member?.user.email}?body=${encodeURI(authorInviteLink)}`}*/}
							{/*	variant={"outlined"}*/}
							{/*	color={"success"}*/}
							{/*>*/}
							{/*	Open e-mail client*/}
							{/*</Button>*/}
						</Box>
					</Stack>
				}
			</DialogContent>
			<DialogActions
				sx={{display: "flex", gap: "2rem", justifyContent: "flex-end", width: "100%"}}
			>
				{ !authorInvite &&
					<Button
						onClick={handleSubmit}
						variant={"contained"}
						color={"primary"}
						disabled={Object.keys(member.selectedNoteIds).length === 0}
					>
						{isAddingNewMember && member?.user.id > 0 ?
							"Invite Member"
							:
							member?.user.id === -1 && isInvitingAuthor ?
								"Generate Author Invite"
								:
								"Save Changes"
						}
					</Button>
				}
				<Button
					color={authorInvite ? "primary" : "error"}
					onClick={() => dispatch(clearManageWorkspaceView())}
				>
					{authorInvite ? "Done" : "Cancel"}
				</Button>

			</DialogActions>
		</ManageModalContainer>
	)
}

function NotePermissionsControls({isReadOnly}) {
	const dispatch = useDispatch()
	return (
		<Box sx={{display: "flex", gap: "2rem", justifyContent: "space-between", width: "100%"}}>
			<Button
				sx={{ width: "100%", fontSize: "1rem", lineHeight: "1.18rem", paddingY: "12px", minWidth: "fit-content" }}
				variant={"outlined"}
				onClick={() => dispatch(resetTopLevelNotePermissions())}
			>
				Select None
			</Button>
			<Button
				sx={{ width: "100%", fontSize: "1rem", lineHeight: "1.18rem", paddingY: "12px", minWidth: "fit-content" }}
				variant={"outlined"}
				onClick={() => dispatch(setTopLevelNotePermissionsAllRead())}
			>
				Select All as Viewable
			</Button>
			{ isReadOnly === false && (
				<Button
					sx={{ width: "100%", fontSize: "1rem", lineHeight: "1.18rem", paddingY: "12px", minWidth: "fit-content" }}
					variant={"outlined"}
					onClick={() => dispatch(setTopLevelNotePermissionsAllEdit())}
				>
					Select All as Editable
				</Button>
			)}
		</Box>
	)
}